import { LangSwitch } from '../comp/chooseLang/ChooseLang';
import type { PageContent } from '../comp/chooseLang/ChooseLang';
import ImageParagraph from '../comp/imgParagraph/ImageParagraph';
import './Global.css'

const pageContentPl: PageContent[] = [
    {
        header: 'Na stronie (kliknij aby przejść):',
        type: 'pageNav',
        content: '#o-projekcie, #moduly, #raytracing-wokseli, #demo',
        custom: null
    },

    // ---------- O projekcie ----------
    {
        header: '#o-projekcie',
        type: 'full',
        content: 'Projekt jest częścią pracy inżynierskiej „Projekt i implementacja silnika gier z wykorzystaniem technologii Vulkan”. Silnik jest podzielony na kilka modułów. Zawiera własny, wieloplatformowy system zarządzania oknami z zachowaniem przełączanym w trakcie działania programu, system wejścia o niskich opóźnieniach oraz renderer oparty na Vulkanie.',
        custom: null
    },
    {
        header: 'Obsługiwane systemy operacyjne',
        type: 'sub',
        content: 'Windows, Linux oraz macOS (podstawowe wsparcie przez MoltenVK).',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Moduły ----------
    {
        header: '#moduly',
        type: 'full',
        content: 'Silnik składa się z pięciu bibliotek.',
        custom: null
    },
    {
        header: 'Core',
        type: 'sub',
        content: 'Framework testowy, narzędzia do debugowania (logger, asercje), definicje wyjątków, skrypty uruchamialne, klasy synchronizacji (synchronizacja czasu), makra atrybutów kompilatora, definicja klasy unknown do globalnego indeksowania klas oraz dołączenia nagłówków systemu operacyjnego, C i C++ (własna implementacja STL pojawi się w przyszłości).',
        custom: null
    },
    {
        header: 'Application',
        type: 'sub',
        content: 'Warstwa abstrakcji zarządzania oknami nad WinAPI, X11 i Cocoa, stworzona przy użyciu wzorca strategii (policy pattern). Do tego zarządzanie wejściem oraz zarządzanie stanem aplikacji, z warunkiem wyjścia deklarowanym przez klasę AppStatus.',
        custom: null
    },
    {
        header: 'Math',
        type: 'sub',
        content: 'Implementacje macierzy i wektorów, operatory C++ do obliczeń wektorowych oraz prymitywy geometryczne.',
        custom: null
    },
    {
        header: 'Rendering',
        type: 'sub',
        content: 'Nakładki C++ na Vulkana, renderer, swapchain, interfejs potoków graficznych, potok raytracingu wokseli, potok renderowania sprite\'ów oraz shadery.',
        custom: null
    },
    {
        header: 'System',
        type: 'sub',
        content: 'Relacje między komponentami silnika, system zadań (job system), system zdarzeń, system refleksji (tylko nazwy klas, z możliwością tworzenia obiektów na podstawie nazwy klasy) oraz osobne punkty wejścia aplikacji dla każdego systemu operacyjnego.',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Raytracing wokseli ----------
    {
        header: '#raytracing-wokseli',
        type: 'full',
        content: 'Do renderowania używam implementacji algorytmu przechodzenia przez woksele opartej na pracy „A Fast Voxel Traversal Algorithm for Ray Tracing” (John Amanatides i Andrew Woo, 1987) oraz oświetlenia Blinna-Phonga. Cały potok działa w compute shaderze.',
        custom: null
    },
    {
        header: 'Dlaczego woksele',
        type: 'sub',
        content: 'Technikę wybrałem na podstawie znajomości podstawowych algorytmów DDA, wcześniejszej pracy z 2D raycastingiem w projekcie AsciiRaycaster oraz podstaw bezpośredniego renderowania obrazu. Wydawała się szybsza i ciekawsza w implementacji niż nauka struktur akceleracji Vulkana i sprzętowego potoku shaderów raytracingu.',
        custom: null
    },
    {
        header: 'Woksele jako struktura akceleracji',
        type: 'sub',
        content: 'Potok miał pozostać w tej formie, dopóki nie pojawiła się sugestia dodania zewnętrznej fizyki. Trzeba było wtedy przejść od sześcianów zbudowanych z wokseli do jakiejś niezależnej struktury. Pomysł polegał na traktowaniu samych wokseli jako struktury akceleracji, co pozwoliło oddzielić figury geometryczne (w tym przypadku sześciany) od siatki wokseli.',
        custom: null
    },
    {
        header: 'Potok sprite\'ów',
        type: 'sub',
        content: 'W trakcie tworzenia. Wykorzystuje vertex shader i fragment shader.',
        custom: null
    },
    {
        header: 'Prototypy',
        type: 'sub',
        content: 'Projekt korzysta z prototypów systemów dostępnych na moim profilu: menedżer zasobów, definicja klasy unknown do globalnego indeksowania klas, system komponentów oraz obsługa zdarzeń.',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Demo ----------
    {
        header: '#demo',
        type: 'full',
        content: 'W demie można zagrać w prostą grę budowlaną, w której stawia się i usuwa bloki. Demo używa Jolt Physics, aby pokazać możliwości projektu i przetestować wydajność.',
        custom: null
    },
    {
        header: '',
        type: 'custom',
        content: '',
        custom: <ImageParagraph
            Header='Fizyka w demie'
            Paragrahps={<a>Bloki reagują na siebie dzięki Jolt Physics, a renderer rysuje je w czasie rzeczywistym w potoku raytracingu wokseli.</a>}
            Img='/b3-promo.gif'
            Type=''
            MaxHeight={500} />
    },
];

const pageContentEn: PageContent[] = [
    {
        header: 'On this site (click to scroll):',
        type: 'pageNav',
        content: '#about, #modules, #voxel-raytracing, #demo',
        custom: null
    },

    // ---------- About ----------
    {
        header: '#about',
        type: 'full',
        content: 'This project is part of the BEng Thesis “Design and Implementation of a Game Engine Using Vulkan Technology”. The engine is separated into several modules. It implements a custom cross-platform window management system with runtime-switchable behavior, a low-latency input system and a Vulkan-based renderer.',
        custom: null
    },
    {
        header: 'Supported operating systems',
        type: 'sub',
        content: 'Windows, Linux and macOS (basic support through MoltenVK).',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Modules ----------
    {
        header: '#modules',
        type: 'full',
        content: 'The engine is made of five libraries.',
        custom: null
    },
    {
        header: 'Core',
        type: 'sub',
        content: 'Testing framework, debugging tools (logger, assertions), exception definitions, runnable scripts, synchronization classes (time synchronization), compiler attribute macros, an unknown-class definition for global class indexing, and includes of OS, C, and C++ headers (a custom STL implementation will be introduced in the future).',
        custom: null
    },
    {
        header: 'Application',
        type: 'sub',
        content: 'A window management abstraction layer over WinAPI, X11, and Cocoa, created using the policy pattern (strategy pattern). It also covers input management and application status management, with the exit condition declared via the AppStatus class.',
        custom: null
    },
    {
        header: 'Math',
        type: 'sub',
        content: 'Matrix and vector implementations, C++ operators for vector math, and geometric primitives.',
        custom: null
    },
    {
        header: 'Rendering',
        type: 'sub',
        content: 'Vulkan C++ wrappers, the renderer, swapchain, pipeline interface, voxel raytracing pipeline, sprite rendering pipeline, and shaders.',
        custom: null
    },
    {
        header: 'System',
        type: 'sub',
        content: 'Relations between engine components, a job system, an event system, a reflection system (class names only, with the ability to create objects by requesting them via class name), and different application entry points per operating system.',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Voxel raytracing ----------
    {
        header: '#voxel-raytracing',
        type: 'full',
        content: 'For rendering, I use a voxel traversal algorithm implementation based on “A Fast Voxel Traversal Algorithm for Ray Tracing” by John Amanatides and Andrew Woo (1987), together with Blinn-Phong reflection lighting. The whole pipeline works in a compute shader.',
        custom: null
    },
    {
        header: 'Why voxels',
        type: 'sub',
        content: 'I picked this technique based on my knowledge of basic DDA algorithms, my earlier work with 2D ray casting in a project called AsciiRaycaster, and some basic knowledge about direct image rendering. It seemed faster and more interesting to implement than learning Vulkan’s Acceleration Structures and the hardware ray tracing shader pipeline.',
        custom: null
    },
    {
        header: 'Voxels as an acceleration structure',
        type: 'sub',
        content: 'The pipeline was supposed to stay in this form until a suggestion to implement external physics appeared. At that point I had to figure out how to switch from cubes made of voxels to some kind of independent structure. The idea was to start treating the voxels themselves as an acceleration structure, which let me decouple geometric figures (in this case cubes) from the voxel grid.',
        custom: null
    },
    {
        header: 'Sprite pipeline',
        type: 'sub',
        content: 'Work in progress. It uses a vertex and a fragment shader.',
        custom: null
    },
    {
        header: 'Prototypes',
        type: 'sub',
        content: 'The project uses system prototypes that are available on my profile: an assets manager, an unknown-class definition for global class indexing, a component system, and an event handler.',
        custom: null
    },
    {
        header: '',
        type: 'spacer',
        content: '',
        custom: null
    },

    // ---------- Demo ----------
    {
        header: '#demo',
        type: 'full',
        content: 'In the demo you can play a simple builder game where you place and remove blocks. It uses Jolt Physics to showcase the project and test its performance.',
        custom: null
    },
    {
        header: '',
        type: 'custom',
        content: '',
        custom: <ImageParagraph
            Header='Physics showcase'
            Paragrahps={<a>Blocks react to each other thanks to Jolt Physics, while the renderer draws them in real time in the voxel raytracing pipeline.</a>}
            Img='/b3-promo.gif'
            Type=''
            MaxHeight={500} />
    }
];

function B3Page() {
    var m = new Map<string, PageContent[]>();
    m.set('en', pageContentEn);
    m.set('pl', pageContentPl);

    return (
        <>
            <LangSwitch LangMap={m} />
        </>
    );
};

export default B3Page;
